import { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';

const noise2D = createNoise2D();

function Nucleus() {
  const meshRef = useRef();
  const originalPos = useRef();

  useEffect(() => {
    if (!meshRef.current) return;
    const pos = meshRef.current.geometry.attributes.position;
    originalPos.current = new Float32Array(pos.array);
  }, []);

  useFrame(({ clock }) => {
    if (!meshRef.current || !originalPos.current) return;

    const t = clock.getElapsedTime();
    const elapsedMs = t * 1000; // match Date.now() style timing

    // Only start nucleus animation after ~6 seconds (6000 ms)
    if (elapsedMs < 6000) return;

    const animationProgress = Math.min(1, (elapsedMs - 6000) / 2000); // 2s fade-in

    const pos = meshRef.current.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const ix = i * 3;
      const x = originalPos.current[ix];
      const y = originalPos.current[ix + 1];
      const z = originalPos.current[ix + 2];

      const len = Math.hypot(x, y, z);
      if (len === 0) continue;

      const nx = x / len;
      const ny = y / len;
      const nz = z / len;

      // blob displacement (same noise scale & speed as original)
      const displacement = 20 + noise2D(nx + t * 0.0004, ny + t * 0.0004) * 3 * animationProgress;

      pos.array[ix]     = nx * displacement;
      pos.array[ix + 1] = ny * displacement;
      pos.array[ix + 2] = nz * displacement;
    }

    pos.needsUpdate = true;
    meshRef.current.geometry.computeVertexNormals();
  });

  return (
    <mesh ref={meshRef}>
      <icosahedronGeometry args={[20, 28]} />
      <meshPhongMaterial
        color="#4488ff"
        emissive="#2266cc"
        emissiveIntensity={0.5}
        shininess={30}
      />
    </mesh>
  );
}

function BackgroundSphere() {
  const tex = useTexture('https://i.ibb.co/HC0vxMw/sky2.jpg');
  return (
    <mesh scale={200}>
      <sphereGeometry args={[1, 64, 64]} />
      <meshBasicMaterial map={tex} side={THREE.BackSide} />
    </mesh>
  );
}

function StarsField() {
  return (
    <points>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={2000}
          array={new Float32Array(
            Array.from({ length: 2000 * 3 }, () => THREE.MathUtils.randFloatSpread(400))
          )}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.8}
        color="#ffffff"
        transparent
        opacity={0.7}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
      />
    </points>
  );
}

export default function SpaceNucleusAnimation() {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 150], fov: 55 }}
        gl={{ antialias: true, powerPreference: 'high-performance' }}
      >
        <color attach="background" args={['#000']} />

        <BackgroundSphere />
        {/* <Nucleus /> */}
        <StarsField />

        {/* Lights */}
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 20, -10]} intensity={2} color="#ffffff" />
        <pointLight position={[0, 0, 0]} intensity={1.8} color="#aaccff" distance={150} />

        <OrbitControls
          autoRotate
          autoRotateSpeed={0.8}
          enablePan={false}
          minDistance={80}
          maxDistance={300}
        />
      </Canvas>
    </div>
  );
}